import { format, isBefore, isSameDay } from 'date-fns';
import { lookupsJson, paymentType } from '@ourbranch/lookups';

import { get, policyTypes } from 'core/helpers/lookups';
import { capitalizeWords, currencyFormatter, getTotalPrice } from 'core/helpers/formatters';
import { scheduleInstallmentLabel } from 'common/components/payment/view-schedule/view-schedule.helpers';

export const BillingLoad = {
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed'
};

const getPolicyTypeName = get(policyTypes);
const getPaymentTypeName = get(lookupsJson.paymentType);

export const getPolicyLine = (policy) => getPolicyTypeName(policy.policyType) ?? 'Unknown';

export const joinPolicyLines = (policies) => policies.map(getPolicyLine).join(' & ');

// "Monthly on the 3rd"; the other plans by their lookup name.
export const getPlanLabel = ({ paymentType: planType, billingDayOfMonth }) => {
  const name = getPaymentTypeName(planType);
  if (planType === paymentType.Monthly && billingDayOfMonth) {
    return `${name} on the ${format(new Date(2000, 0, billingDayOfMonth), 'do')}`;
  }
  return name ?? null;
};

// Same fallback as the policy page's billing history: payloads from before `isRefund` existed.
const isRefundRow = (transaction) => transaction.isRefund ?? Boolean(transaction.id?.startsWith('re_'));

const statusLabel = (status) => (status ? capitalizeWords(status) : '—');

// The policy page's past-due rules (billing-details.jsx): the latest charge failed with a balance
// left, or an escrow policy is past its start date with an unpaid charge.
export const isPastDue = ({ policy, billing }) => {
  const transactions = billing.transactions ?? [];
  if (!(billing.totalRemaining > 0)) {
    return false;
  }
  if (transactions[0]?.paymentStatus === 'failed') {
    return true;
  }
  const effectiveDate = new Date(policy.effectiveDate);
  return (
    policy.paymentType === paymentType.Escrow &&
    transactions.some((transaction) => transaction.paymentStatus === 'unpaid') &&
    !isBefore(new Date(), effectiveDate) &&
    !isSameDay(new Date(), effectiveDate)
  );
};

const amountDue = (billing) => billing.nextPaymentTotal ?? billing.nextPaymentAmount ?? 0;

// The earliest upcoming payment across the household, adding up every policy due that day.
const getNextPayment = (loaded) => {
  const due = loaded.filter(({ billing }) => billing.nextPaymentDate && amountDue(billing) > 0);
  if (!due.length) {
    return null;
  }
  const date = due.map(({ billing }) => billing.nextPaymentDate).sort()[0];
  const sameDay = due.filter(({ billing }) => billing.nextPaymentDate === date);
  return {
    date,
    amount: sameDay.reduce((total, { billing }) => total + amountDue(billing), 0),
    policies: sameDay.map(({ policy }) => policy)
  };
};

const hasMethod = (method) => Boolean(method?.id || method?.last4);

/**
 * Rolls the per-policy billing results up for the summary. Totals and the next payment come only
 * from a complete set: while any policy is loading or failed they're left out, so a figure never
 * silently omits a policy.
 */
export const getBillingSummary = ({ policies, results }) => {
  const statusOf = (policy) => results[policy.id]?.status ?? BillingLoad.LOADING;
  const loaded = policies
    .filter((policy) => statusOf(policy) === BillingLoad.LOADED)
    .map((policy) => ({ policy, billing: results[policy.id].billingDetails }));
  const complete = loaded.length === policies.length;

  const defaultMethods = [];
  for (const { policy, billing } of loaded) {
    const method = billing.activePaymentMethod;
    if (hasMethod(method)) {
      const known = defaultMethods.find((entry) => (entry.method.id ?? null) === (method.id ?? null));
      if (known) {
        known.policies.push(policy);
      } else {
        defaultMethods.push({ method, policies: [policy] });
      }
    }
  }

  return {
    complete,
    loaded,
    failedPolicies: policies.filter((policy) => statusOf(policy) === BillingLoad.FAILED),
    pastDue: complete ? loaded.filter(isPastDue) : [],
    nextPayment: complete ? getNextPayment(loaded) : null,
    defaultMethods
  };
};

// Every saved method across the household's loaded policies, once each.
export const getPaymentMethods = (loaded) => {
  const methods = new Map();
  for (const { billing } of loaded) {
    for (const method of billing.allPaymentMethods ?? []) {
      if (hasMethod(method) && !methods.has(method.id ?? method.last4)) {
        methods.set(method.id ?? method.last4, method);
      }
    }
  }
  return [...methods.values()];
};

// A policy's schedule card: its charges so far, then what's scheduled, oldest first.
export const getScheduleRows = (billing) => {
  const charges = (billing.transactions ?? [])
    .filter((transaction) => !isRefundRow(transaction))
    .map((transaction, index) => ({
      key: transaction.id ?? `charge-${index}`,
      date: transaction.paymentDate,
      item: transaction.internalDescription || 'Payment',
      amount: currencyFormatter(transaction.paymentAmount),
      status: statusLabel(transaction.paymentStatus)
    }));
  let scheduled = (billing.nextPayments ?? [])
    .filter((payment) => payment.date)
    .map((payment) => ({
      key: `scheduled-${payment.date}`,
      date: payment.date,
      item: 'Scheduled payment',
      amount: scheduleInstallmentLabel(payment),
      status: 'Scheduled'
    }));
  if (!scheduled.length && billing.nextPaymentDate && amountDue(billing) > 0) {
    scheduled = [
      {
        key: `scheduled-${billing.nextPaymentDate}`,
        date: billing.nextPaymentDate,
        item: 'Next payment',
        amount: currencyFormatter(amountDue(billing)),
        status: 'Scheduled'
      }
    ];
  }
  return [...charges, ...scheduled].sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
};

const REFUNDABLE_STATUSES = ['paid', 'partially refunded'];

/**
 * One billing history for the household, newest first. The API doesn't say which charge a refund
 * reverses, so refunds are their own rows. Dates are date-only, so same-day order isn't meaningful.
 */
export const getBillingHistory = (loaded) =>
  loaded
    .flatMap(({ policy, billing }) => {
      const transactions = billing.transactions ?? [];
      const ids = new Set(transactions.map((transaction) => transaction.id).filter(Boolean));
      const refunds = (billing.refundTransactions ?? []).filter((refund) => !refund.id || !ids.has(refund.id));
      return [...transactions, ...refunds].map((transaction, index) => {
        const refund = isRefundRow(transaction);
        return {
          key: `${policy.id}:${transaction.id ?? index}`,
          policy,
          isRefund: refund,
          date: (refund ? transaction.refundedAt : transaction.paymentDate) ?? transaction.paymentDate ?? null,
          item: transaction.internalDescription || (refund ? 'Refund' : 'Payment'),
          transaction,
          amount: refund ? -(transaction.paymentRefunded ?? 0) : (transaction.paymentAmount ?? 0),
          // A refunded charge reads as the payment it was; its refund has its own row.
          status:
            !refund && ['refunded', 'partially refunded'].includes(transaction.paymentStatus)
              ? 'Paid'
              : statusLabel(transaction.paymentStatus),
          refundable:
            !refund &&
            REFUNDABLE_STATUSES.includes(transaction.paymentStatus) &&
            (transaction.paymentAmount ?? 0) > (transaction.paymentRefunded ?? 0)
        };
      });
    })
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));

export const getPriceRows = (policy) => {
  const fees = policy.fees ?? [];
  return {
    rows: [
      { key: 'premium', label: 'Premium', amount: policy.premium ?? 0 },
      // Brands without a surplus contribution never show or mention it (#33827).
      ...(policy.surplusContribution > 0
        ? [{ key: 'surplus', label: 'Surplus', amount: policy.surplusContribution }]
        : []),
      ...fees.map((fee, index) => ({ key: `fee-${index}`, label: fee.description || 'Fee', amount: fee.amount ?? 0 }))
    ],
    total: getTotalPrice(policy.premium ?? 0, fees, policy.surplusContribution ?? 0)
  };
};
